import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';

function EventMetadataDefinitionEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    getEventMetadataDefinitions,
    createEventMetadataDefinition,
    updateEventMetadataDefinition,
    isLoading,
    generateCacheKey
  } = useData();
  
  const isEditing = Boolean(id);
  
  const [formData, setFormData] = useState({
    fieldName: '',
    fieldType: 'TEXT',
    description: '',
    isRequired: false,
    defaultValue: '',
    validationRules: '',
    displayOrder: 1
  });
  const [error, setError] = useState(null);

  const cacheKey = generateCacheKey('event-metadata-definitions');
  const loading = isLoading(cacheKey);

  const fieldTypes = [
    { value: 'TEXT', label: 'Text' },
    { value: 'NUMBER', label: 'Number' },
    { value: 'DATE', label: 'Date' },
    { value: 'DATETIME', label: 'Date & Time' },
    { value: 'BOOLEAN', label: 'Boolean (Yes/No)' },
    { value: 'SELECT', label: 'Select List' },
    { value: 'MULTISELECT', label: 'Multi-Select' },
    { value: 'TEXTAREA', label: 'Text Area' },
    { value: 'URL', label: 'URL' },
    { value: 'EMAIL', label: 'Email' },
    { value: 'TIME', label: 'Time' },
    { value: 'DURATION', label: 'Duration' },
    { value: 'RATING', label: 'Rating (1-5)' }
  ];

  const loadMetadataDefinition = useCallback(async () => {
    try {
      setError(null);
      const definitions = await getEventMetadataDefinitions();
      const definition = definitions.find(def => def.id === parseInt(id));
      
      if (!definition) {
        throw new Error('Metadata definition not found');
      }
      
      setFormData({
        fieldName: definition.fieldName || '',
        fieldType: definition.fieldType || 'TEXT',
        description: definition.description || '',
        isRequired: definition.isRequired || false,
        defaultValue: definition.defaultValue || '',
        validationRules: definition.validationRules || '',
        displayOrder: definition.displayOrder || 1
      });
    } catch (error) {
      console.error('Error loading metadata definition:', error);
      setError('Failed to load metadata definition. Please try again.');
    }
  }, [id, getEventMetadataDefinitions]);

  useEffect(() => {
    if (isEditing) {
      loadMetadataDefinition();
    }
  }, [id, isEditing, loadMetadataDefinition]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      const submitData = {
        ...formData,
        displayOrder: parseInt(formData.displayOrder) || 1
      };

      if (isEditing) {
        await updateEventMetadataDefinition(id, submitData);
        console.log('Event metadata definition updated via DataContext');
      } else {
        await createEventMetadataDefinition(submitData);
        console.log('Event metadata definition created via DataContext');
      }

      navigate('/settings/event-metadata-definitions');
    } catch (error) {
      console.error('Error saving metadata definition:', error);
      setError(error.message || 'Failed to save metadata definition. Please try again.');
    }
  };

  const handleCancel = () => {
    navigate('/settings/event-metadata-definitions');
  };

  if (loading && isEditing) {
    return (
      <div className="settings-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading metadata definition...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-container">
      <h2>{isEditing ? 'Edit Event Metadata Definition' : 'Create Event Metadata Definition'}</h2>
      
      {error && (
        <div className="error-message" style={{ 
          color: '#dc3545', 
          backgroundColor: 'var(--intent-danger-bg, #f8d7da)', 
          border: '1px solid #f5c6cb', 
          borderRadius: '4px', 
          padding: '0.75rem',
          marginBottom: '1rem'
        }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="fieldName">Field Name *</label>
          <input
            type="text"
            id="fieldName"
            name="fieldName"
            value={formData.fieldName}
            onChange={handleInputChange}
            required
            disabled={loading}
            placeholder="Enter field name (e.g., 'Location', 'Attendance', 'Notes')"
          />
        </div>

        <div className="form-group">
          <label htmlFor="fieldType">Field Type *</label>
          <select
            id="fieldType"
            name="fieldType"
            value={formData.fieldType}
            onChange={handleInputChange}
            required
            disabled={loading}
          >
            {fieldTypes.map(type => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            disabled={loading}
            placeholder="Enter field description"
            rows="3"
          />
        </div>

        <div className="form-group">
          <label htmlFor="defaultValue">Default Value</label>
          <input
            type="text"
            id="defaultValue"
            name="defaultValue"
            value={formData.defaultValue}
            onChange={handleInputChange}
            disabled={loading}
            placeholder="Enter default value (optional)"
          />
        </div>

        <div className="form-group">
          <label htmlFor="validationRules">Validation Rules</label>
          <textarea
            id="validationRules"
            name="validationRules"
            value={formData.validationRules}
            onChange={handleInputChange}
            disabled={loading}
            placeholder="Enter validation rules (JSON format, optional)"
            rows="3"
          />
          <small style={{ color: '#6c757d', fontSize: '0.875rem' }}>
            Example: {"{"}"min": 0, "max": 100{"}"} for rating fields
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="displayOrder">Display Order</label>
          <input
            type="number"
            id="displayOrder"
            name="displayOrder"
            value={formData.displayOrder}
            onChange={handleInputChange}
            disabled={loading}
            min="1"
            step="1"
          />
        </div>

        <div className="form-group">
          <label>
            <input
              type="checkbox"
              name="isRequired"
              checked={formData.isRequired}
              onChange={handleInputChange}
              disabled={loading}
            />
            Required Field
          </label>
        </div>

        <div className="form-actions" style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
          <button 
            type="submit" 
            disabled={loading || !formData.fieldName.trim()}
            style={{ opacity: loading ? 0.6 : 1 }}
          >
            {loading ? 'Saving...' : (isEditing ? 'Update Definition' : 'Create Definition')}
          </button>
          
          <button 
            type="button" 
            className="secondary" 
            onClick={handleCancel}
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default EventMetadataDefinitionEdit;